import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../services/api";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle, AlertCircle, Heart, Zap, Brain } from "lucide-react";

const MOOD_EMOJIS    = ["", "😞", "😕", "😐", "🙂", "😄"];
const ENERGY_EMOJIS  = ["", "🪫", "😴", "⚡", "🔋", "🚀"];
const STRESS_EMOJIS  = ["", "😌", "😊", "😬", "😰", "🤯"];

const BURNOUT_COLORS = {
  low:    "text-green-600 bg-green-50 border-green-200",
  medium: "text-orange-500 bg-orange-50 border-orange-200",
  high:   "text-red-600 bg-red-50 border-red-200",
};

function ScoreSlider({ label, value, onChange, emojis, icon: Icon, color }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Icon className={`h-4 w-4 ${color}`} />
          {label}
        </div>
        <span className="text-2xl">{emojis[value] || ""}</span>
      </div>
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            className={`flex-1 h-10 rounded-lg text-sm font-bold border-2 transition-all ${
              value === n
                ? "border-blue-500 bg-blue-500 text-white scale-105"
                : "border-border bg-muted/50 text-muted-foreground hover:border-blue-300"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function BurnoutBadge({ risk }) {
  const { t } = useTranslation();
  if (!risk) return null;
  const level = risk.risk_level || risk.level;
  const score = risk.risk_score ?? risk.score;
  const levelKey = BURNOUT_COLORS[level] ? level : "low";
  return (
    <div className={`rounded-xl border p-4 ${BURNOUT_COLORS[levelKey]}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide mb-1">{t("portal.wellbeing.burnoutRisk")}</div>
          <div className="text-2xl font-bold">{score}<span className="text-sm font-normal">/100</span></div>
          <div className="text-sm font-semibold mt-0.5">{t(`portal.wellbeing.riskLevels.${levelKey}`)}</div>
        </div>
        <div className="text-4xl">{level === "low" ? "💚" : level === "medium" ? "🟡" : "🔴"}</div>
      </div>
      {risk.factors?.length > 0 && (
        <div className="mt-3 space-y-0.5">
          {risk.factors.map((f, i) => (
            <div key={i} className="text-xs flex items-center gap-1">
              <AlertCircle className="h-3 w-3 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MonBienEtre() {
  const { t } = useTranslation();
  const [checkin, setCheckin] = useState({ mood_score: 3, energy_score: 3, stress_score: 2, note: "", is_anonymous: false });
  const [risk, setRisk] = useState(null);
  const [todayCheckin, setTodayCheckin] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, h] = await Promise.all([
        apiClient.get("v1/wellbeing/burnout/me").catch(() => null),
        apiClient.get("v1/wellbeing/checkins/me?days=7").catch(() => ({ data: [] })),
      ]);
      if (r) setRisk(r.data);
      const checkins = h.data || [];
      setHistory(checkins);
      const today = new Date().toISOString().split("T")[0];
      const tc = checkins.find(c => c.checkin_date === today);
      if (tc) {
        setTodayCheckin(tc);
        setCheckin({ mood_score: tc.mood_score, energy_score: tc.energy_score, stress_score: tc.stress_score, note: tc.note || "", is_anonymous: tc.is_anonymous });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await apiClient.post("v1/wellbeing/checkins", checkin);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      load();
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-muted-foreground">
      <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t("common.loading")}
    </div>
  );

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        {t("portal.wellbeing.intro")}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Check-in form */}
        <div className="space-y-5 bg-card rounded-xl border p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">{t("portal.wellbeing.todayCheckin")}</h3>
            {todayCheckin && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle className="h-3.5 w-3.5" /> {t("portal.wellbeing.alreadyDone")}
              </span>
            )}
          </div>

          <ScoreSlider label={t("portal.wellbeing.mood")} value={checkin.mood_score} onChange={v => setCheckin(c => ({ ...c, mood_score: v }))} emojis={MOOD_EMOJIS} icon={Heart} color="text-pink-500" />
          <ScoreSlider label={t("portal.wellbeing.energy")} value={checkin.energy_score} onChange={v => setCheckin(c => ({ ...c, energy_score: v }))} emojis={ENERGY_EMOJIS} icon={Zap} color="text-yellow-500" />
          <ScoreSlider label={t("portal.wellbeing.stress")} value={checkin.stress_score} onChange={v => setCheckin(c => ({ ...c, stress_score: v }))} emojis={STRESS_EMOJIS} icon={Brain} color="text-purple-500" />

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">{t("portal.wellbeing.noteOptional")}</label>
            <textarea
              className="w-full text-sm border rounded-lg px-3 py-2 bg-background resize-none focus:outline-none focus:ring-1 focus:ring-ring"
              rows={2}
              value={checkin.note}
              onChange={e => setCheckin(c => ({ ...c, note: e.target.value }))}
              placeholder={t("portal.wellbeing.notePlaceholder")}
            />
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="anon" checked={checkin.is_anonymous} onChange={e => setCheckin(c => ({ ...c, is_anonymous: e.target.checked }))} />
            <label htmlFor="anon" className="text-xs text-muted-foreground cursor-pointer">{t("portal.wellbeing.anonymous")}</label>
          </div>

          <Button onClick={handleSubmit} disabled={saving} className="w-full">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : saved ? <CheckCircle className="h-4 w-4 mr-2" /> : null}
            {saved ? t("portal.wellbeing.saved") : todayCheckin ? t("portal.wellbeing.update") : t("portal.wellbeing.submitCheckin")}
          </Button>
        </div>

        {/* Risk + history */}
        <div className="space-y-4">
          {risk && <BurnoutBadge risk={risk} />}

          {history.length > 0 && (
            <div className="bg-card rounded-xl border p-5">
              <h3 className="font-semibold text-sm mb-3">{t("portal.wellbeing.last7Days")}</h3>
              <div className="space-y-2">
                {history.slice(0, 7).map((h) => (
                  <div key={h.id} className="flex items-center gap-3">
                    <div className="text-xs text-muted-foreground w-20 flex-shrink-0">{h.checkin_date}</div>
                    <div className="flex gap-1 flex-1">
                      {[
                        { val: h.mood_score, emoji: MOOD_EMOJIS, label: t("portal.wellbeing.mood") },
                        { val: h.energy_score, emoji: ENERGY_EMOJIS, label: t("portal.wellbeing.energy") },
                        { val: h.stress_score, emoji: STRESS_EMOJIS, label: t("portal.wellbeing.stress") },
                      ].map((item, i) => (
                        <div key={i} className="flex-1 text-center">
                          <div className="text-base">{item.emoji[item.val]}</div>
                          <div className="text-xs text-muted-foreground">{item.val}/5</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
